import React from "react";
//#region redux
import { useSelector } from "react-redux";
//#endregion
//#region types
import { DimensionTypeItem } from "models/propTypes/dimensionPropTypes";
import { TranslationKeys } from "translations";
import { DimensionInputSelectorExtraProps, FormFieldProps } from "models/propTypes/genericFormProps";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
//#endregion
//#region components
import useMountedState from "../../useMountedState";
import AutoCompleteInput from "../baseFields/autoCompleteInput";
import ReadOnlyField from "../baseFields/readOnlyField";
import { FormControl, Grid } from "@mui/material";
import { useFormikContext } from "formik";
//#endregion
import { DimensionHelper, LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

function DimensionInputSelectorFormField<T extends Record<string, any>>(props: FormFieldProps<T>) {
  const { name, field, value, touched, disabled, readonly } = props;
  const { getMounted } = useMountedState();
  const [dimensionMap, setDimensionMap] = React.useState<DimensionTypeItem<AutocompleteListItem>[]>([]);
  const formikContext = useFormikContext<T>();

  const { type, allowDefaultDimensions, onItemSelect, allowedTypes, translateDimensionLabels } =
    field.extraProps as DimensionInputSelectorExtraProps;

  const { dimensionTypes, dimensionSuggestions, dimensions } = useSelector(
    DimensionHelper.getAppStateDimensionSelector(type)
  );

  React.useEffect(() => {
    getMounted() && setDimensionMap(getDimensionTypeMap());
  }, [dimensionTypes, dimensions, dimensionSuggestions, value]);

  const getDimensionTypeMap = (): DimensionTypeItem<AutocompleteListItem>[] => {
    const selectedDimensionIds = props.value ?? [];
    let selectedUpdated = false;

    const allowedDimensionTypes = allowedTypes?.length
      ? dimensionTypes.filter((d) => allowedTypes.includes(d.type))
      : dimensionTypes;

    const dimensionsMap = allowedDimensionTypes.map((companyDimType): DimensionTypeItem<AutocompleteListItem> => {
      const all = dimensions.find((d) => d.key.id === companyDimType.id);
      const suggestions = dimensionSuggestions.find((ds) => ds.key.id === companyDimType.id)?.value ?? [];
      const type = all?.key ?? companyDimType;

      let selected = all && all.value.find((d) => d.id && selectedDimensionIds.includes(d.id));

      if (
        (!dimensionMap || dimensionMap.length === 0) &&
        allowDefaultDimensions &&
        !touched &&
        name &&
        selected === undefined &&
        !!type.recommendedDimensionId
      ) {
        selectedUpdated = true;
        const item = all?.value.find((item) => item.id === type.recommendedDimensionId);
        selected = item;
      }

      return {
        id: type.id ?? "",
        name: type.name ?? "",
        type: type.type,
        compatibilityType: type.compatibilityType,
        suggestions: suggestions.map((dim): AutocompleteListItem => ({ id: dim.id, value: dim.name ?? "" })),
        selectedDimension: selected ? { id: selected.id, value: selected.name ?? "" } : undefined,
        all:
          all?.value.map(
            (dim): AutocompleteListItem => ({
              id: dim.id,
              value: dim.name ?? "",
            })
          ) ?? [],
      };
    });

    if (selectedUpdated) raiseOnChangeEvent(dimensionsMap);
    return dimensionsMap;
  };

  const onDimensionSelect = React.useCallback(
    (dimensionTypeId: string, selectedDimension: AutocompleteListItem | null) => {
      const dimensionsNew = [...dimensionMap];
      const dimension = dimensionsNew.find((x) => x.id === dimensionTypeId);
      if (dimension) {
        dimension.selectedDimension = dimension && dimension.all.find((d) => d.id === selectedDimension?.id);

        onItemSelect && onItemSelect(selectedDimension, dimensionTypeId, name, formikContext);
        raiseOnChangeEvent(dimensionsNew);
      }
    },
    [dimensionMap]
  );

  const raiseOnChangeEvent = (dimensionsUpdated: DimensionTypeItem<AutocompleteListItem>[]) => {
    props.handleOnChangeCustom(
      name,
      dimensionsUpdated
        .filter((x) => x.selectedDimension !== undefined)
        .flatMap((dim) => dim.selectedDimension?.id) as string[]
    );
  };

  const getValue = () => {
    return dimensionMap
      .flatMap((dim) => dim.selectedDimension?.value)
      .filter((x) => !!x)
      .join(", ");
  };

  const getAutoCompleteInput = (
    dimensionType: DimensionTypeItem<AutocompleteListItem>,
    label: string,
    placeholder?: string
  ) => {
    return (
      <AutoCompleteInput
        key={`${dimensionType.name}`}
        label={label}
        placeholder={placeholder}
        value={dimensionType.selectedDimension?.value}
        name={dimensionType.name}
        onItemSelect={onDimensionSelect.bind(null, dimensionType.id)}
        items={dimensionType.all}
        error={
          typeof props.errors !== "string" || props.errors.search(dimensionType.name) !== -1 ? props.errors : undefined
        }
        disabled={disabled}
        onFocus={field.onFocusHandler}
        onBlur={props.handleOnBlurCustom.bind(null, name)}
      />
    );
  };

  const dimensionLabel = (dimension: DimensionTypeItem<AutocompleteListItem>) =>
    translateDimensionLabels
      ? DimensionHelper.getTranslatedDimensionTypeText(dimension.type, dimension.name, dimension.compatibilityType)
      : DimensionHelper.getDimensionTypeText(dimension.type, dimension.name);

  return (
    <>
      {!readonly ? (
        <Grid container spacing={1}>
          {dimensionMap.map((dimension: DimensionTypeItem<AutocompleteListItem>, index) => {
            return props.layout ? (
              <props.layout.type
                key={dimension.id}
                {...props.layout.props}
                label={dimensionLabel(dimension)}
              >
                <FormControl fullWidth className={cssExports.inputFormControl}>
                  {getAutoCompleteInput(
                    dimension,
                    props.layout.props?.inline || !props.layout.props?.enableControlLables
                      ? dimensionLabel(dimension)
                      : " ",
                    `${LocalizationHelperInstance.getFormattedMessage(
                      TranslationKeys.Common.Search
                    )} ${dimensionLabel(dimension)}...`
                  )}
                </FormControl>
              </props.layout.type>
            ) : (
              <Grid key={index} item xs={6}>
                {getAutoCompleteInput(dimension, dimensionLabel(dimension))}
              </Grid>
            );
          })}
        </Grid>
      ) : (
        <ReadOnlyField href={field.href}>{getValue()}</ReadOnlyField>
      )}
    </>
  );
}

export default DimensionInputSelectorFormField;
